import {
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Rounded shell column (topbar + main) — for horizontal bounds; sibling of the nav sidebar. */
function queryAppShellContentCapsule(): HTMLElement | null {
  const root = document.getElementById('root');
  if (!root) return null;
  const main = root.querySelector('main.min-h-0.flex-1.overflow-y-auto');
  const parent = main?.parentElement;
  return parent instanceof HTMLElement ? parent : null;
}

/** Scrollable list/detail outlet — vertical bounds for preview drawer (below topbar, Lexicon-style). */
function queryAppShellScrollMain(): HTMLElement | null {
  const root = document.getElementById('root');
  if (!root) return null;
  const main = root.querySelector('main.min-h-0.flex-1.overflow-y-auto');
  return main instanceof HTMLElement ? main : null;
}

type CapsuleLayout = {
  capsule: { top: number; left: number; width: number; height: number };
  vw: number;
};

function readCapsuleLayout(): CapsuleLayout | null {
  const shell = queryAppShellContentCapsule();
  const main = queryAppShellScrollMain();
  if (!shell || !main) return null;
  const sr = shell.getBoundingClientRect();
  const mr = main.getBoundingClientRect();
  return {
    capsule: {
      top: mr.top,
      left: sr.left,
      width: sr.width,
      height: mr.height,
    },
    vw: window.innerWidth,
  };
}

export type DrawerProps = {
  open: boolean;
  onClose: () => void;
  side?: 'right' | 'left';
  /** Tailwind width classes, e.g. `w-full max-w-[480px]` — used only when {@link clipToAppShellCapsule} is false or capsule is not found */
  widthClass?: string;
  /**
   * When true (default), backdrop + panel are clipped to the main content capsule so the nav sidebar stays visible (Lexicon-style split).
   * Falls back to full-viewport overlay if the capsule node cannot be resolved.
   */
  clipToAppShellCapsule?: boolean;
  /** Max drawer width in px when clipped to capsule; also capped by capsule width */
  panelMaxWidthPx?: number;
  showBackdrop?: boolean;
  /** When true, clicking the backdrop calls onClose */
  closeOnBackdrop?: boolean;
  children: ReactNode;
  /** Optional title row: if omitted, caller renders full header inside children */
  title?: ReactNode;
  showCloseButton?: boolean;
  /** For aria-labelledby when title is string-only; otherwise use aria-label on panel */
  titleId?: string;
  ariaLabel?: string;
  panelClassName?: string;
};

export function Drawer({
  open,
  onClose,
  side = 'right',
  widthClass = 'w-full max-w-[min(100vw,480px)] sm:max-w-[min(100vw,480px)]',
  clipToAppShellCapsule = true,
  panelMaxWidthPx = 480,
  showBackdrop = true,
  closeOnBackdrop = true,
  children,
  title,
  showCloseButton = true,
  titleId = 'ggj-drawer-title',
  ariaLabel,
  panelClassName,
}: DrawerProps) {
  const location = useLocation();
  const panelRef = useRef<HTMLElement>(null);
  const prevActiveRef = useRef<HTMLElement | null>(null);
  const [capsuleBoundsTick, setCapsuleBoundsTick] = useState(0);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onKeyDown]);

  useEffect(() => {
    if (!open) return;
    prevActiveRef.current = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => {
      const root = panelRef.current;
      if (!root) return;
      const first = root.querySelector(FOCUSABLE) as HTMLElement | null;
      first?.focus();
    }, 50);
    return () => window.clearTimeout(t);
  }, [open]);

  const openPathRef = useRef<string | null>(null);
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      openPathRef.current = location.pathname;
    }
    if (!open) {
      openPathRef.current = null;
    }
    prevOpenRef.current = open;
  }, [open, location.pathname]);

  useEffect(() => {
    if (!open || openPathRef.current == null) return;
    if (location.pathname !== openPathRef.current) {
      onClose();
    }
  }, [location.pathname, open, onClose]);

  useEffect(() => {
    if (open) return;
    const el = prevActiveRef.current;
    if (el && typeof el.focus === 'function') {
      window.setTimeout(() => el.focus(), 0);
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !clipToAppShellCapsule) return;
    const shell = queryAppShellContentCapsule();
    const main = queryAppShellScrollMain();
    if (!shell || !main) return;
    const bump = () => setCapsuleBoundsTick((t) => t + 1);
    const ro = new ResizeObserver(bump);
    ro.observe(shell);
    ro.observe(main);
    window.addEventListener('resize', bump);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', bump);
    };
  }, [open, clipToAppShellCapsule]);

  const capsuleLayout = useMemo(() => {
    if (!open || !clipToAppShellCapsule) return null;
    return readCapsuleLayout();
  }, [open, clipToAppShellCapsule, capsuleBoundsTick]);

  const slideX = side === 'right' ? { closed: '100%', open: '0%' } : { closed: '-100%', open: '0%' };

  const useCapsule = Boolean(clipToAppShellCapsule && capsuleLayout);
  const cap = capsuleLayout?.capsule;
  const rightInset =
    cap != null && capsuleLayout != null ? capsuleLayout.vw - cap.left - cap.width : 0;
  const panelWidthPx = cap != null ? Math.min(panelMaxWidthPx, Math.max(0, cap.width)) : panelMaxWidthPx;

  const backdropStyle = useCapsule && cap
    ? { top: cap.top, left: cap.left, width: cap.width, height: cap.height }
    : undefined;

  const panelStyle =
    useCapsule && cap
      ? {
          top: cap.top,
          height: cap.height,
          width: panelWidthPx,
          ...(side === 'right' ? { right: rightInset } : { left: cap.left }),
        }
      : undefined;

  return (
    <AnimatePresence>
      {open ? (
        <>
          {showBackdrop ? (
            <motion.button
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className={[
                'fixed z-[140] bg-black/40',
                useCapsule ? '' : 'inset-0',
              ]
                .filter(Boolean)
                .join(' ')}
              style={backdropStyle}
              aria-label="Close panel"
              onClick={() => {
                if (closeOnBackdrop) onClose();
              }}
            />
          ) : null}
          <motion.aside
            ref={panelRef as RefObject<HTMLElement>}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            aria-labelledby={title ? titleId : undefined}
            initial={{ x: slideX.closed }}
            animate={{ x: slideX.open }}
            exit={{ x: slideX.closed }}
            transition={{ type: 'tween', duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            style={panelStyle}
            className={[
              'fixed z-[150] flex min-h-0 flex-col overflow-hidden border vscode-border vscode-bg-secondary shadow-2xl',
              useCapsule
                ? side === 'right'
                  ? 'border-l'
                  : 'border-r'
                : ['top-0 h-full', side === 'right' ? 'right-0 border-l' : 'left-0 border-r', widthClass].join(' '),
              panelClassName,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {title != null || showCloseButton ? (
              <header className="flex shrink-0 items-center justify-between gap-3 border-b vscode-border px-4 py-3">
                <div id={titleId} className="min-w-0 flex-1 text-base font-semibold vscode-text-primary">
                  {title}
                </div>
                {showCloseButton ? (
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg p-2 vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                ) : null}
              </header>
            ) : null}
            {children}
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}
